import React, { useState, useEffect, useMemo, useRef } from 'react';
import './TeacherHome.css';
import { useAuth } from '../context/AuthContext';
import { engine } from '../core/books/storage.js';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import {
  useExams,
  toNumber,
  calculatePassThreshold,
} from '../components/Exams/ExamContext.jsx';
import {
  BooksEngineProvider,
  useMyProjects,
  useMyRequests,
  PROJECT_STATE,
  REQUEST_STATUS,
} from '../hooks/useBooksEngine.js';

// ================================================================
// Helpers
// ================================================================
const toFaNum = (n) => {
  if (n === undefined || n === null) return '۰';
  return String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 10) return 'morning';
  if (h >= 10 && h < 15) return 'day';
  if (h >= 15 && h < 20) return 'evening';
  return 'night';
};

const getPersianGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 10) return 'صبح بخیر';
  if (h >= 10 && h < 15) return 'ظهر بخیر';
  if (h >= 15 && h < 20) return 'عصر بخیر';
  return 'شب بخیر';
};

// ================================================================
// Weather bootstrap (per-user)
// ================================================================
const WEATHER_DEFAULT_CITY = {
  id: 'tehran',
  name: 'تهران',
  country: 'ایران',
  admin1: 'تهران',
  latitude: 35.6892,
  longitude: 51.389,
  timezone: 'Asia/Tehran',
};

const WEATHER_DEFAULT_SETTINGS = {
  tempUnit: 'celsius',
  windUnit: 'kmh',
  darkMode: false,
  timeFormat: '24',
};

const ensureWeatherStorage = (userId) => {
  try {
    const keyCities = `weather_cities_v2:${userId}`;
    const keyPrimary = `weather_primary_city_v2:${userId}`;
    const keyActive = `weather_active_city_v2:${userId}`;
    const keySettings = `weather_settings_v2:${userId}`;

    const existing = engine.get(keyCities, null);
    if (!Array.isArray(existing) || existing.length === 0) {
      engine.set(keyCities, [WEATHER_DEFAULT_CITY]);
      engine.set(keyPrimary, WEATHER_DEFAULT_CITY.id);
      engine.set(keyActive, '0');
    }
    if (!engine.get(keyPrimary, null)) {
      engine.set(keyPrimary, WEATHER_DEFAULT_CITY.id);
    }
    if (!engine.get(keySettings, null)) {
      engine.set(keySettings, WEATHER_DEFAULT_SETTINGS);
    }
  } catch {}
};

// ================================================================
// Weather data (city display under Weather tile)
// ================================================================
const useWeatherTile = (user) => {
  const userId = user?.id || 'guest';
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const keyCities = `weather_cities_v2:${userId}`;
    const keyPrimary = `weather_primary_city_v2:${userId}`;

    const load = () => {
      try {
        ensureWeatherStorage(userId);
        const primaryId = engine.get(keyPrimary, null);
        const cities = engine.get(keyCities, []);
        const activeCity = primaryId
          ? cities.find((c) => c.id === primaryId) || cities[0]
          : cities[0];
        if (!activeCity) return;

        const cacheKey = `${activeCity.latitude},${activeCity.longitude}`;
        const cache = JSON.parse(localStorage.getItem('weather_cache_v2') || '{}');
        const cached = cache[cacheKey];
        if (cached?.data?.current) {
          setWeather({
            city: activeCity.name,
            temp: Math.round(cached.data.current.temperature_2m ?? 0),
          });
          return;
        }

        const tz = activeCity.timezone === 'auto' ? 'auto' : 'Asia/Tehran';
        const url =
          `https://api.open-meteo.com/v1/forecast?latitude=${activeCity.latitude}&longitude=${activeCity.longitude}` +
          `&current=temperature_2m,apparent_temperature,weather_code,is_day` +
          `&timezone=${tz}&forecast_days=1`;

        fetch(url)
          .then((r) => r.json())
          .then((json) => {
            setWeather({
              city: activeCity.name,
              temp: Math.round(json.current?.temperature_2m ?? 0),
            });
            try {
              const c = JSON.parse(localStorage.getItem('weather_cache_v2') || '{}');
              c[cacheKey] = { ts: Date.now(), data: json };
              localStorage.setItem('weather_cache_v2', JSON.stringify(c));
            } catch {}
          })
          .catch(() => {});
      } catch {}
    };

    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [userId]);

  return weather;
};

// ================================================================
// Private class data (for hero + stats strip)
// ================================================================
const PC_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PC_STATUS = {
  SCHEDULED: 'scheduled',
  LIVE: 'live',
  ENDED: 'ended',
  CANCELLED: 'cancelled',
};

const pcGetStartMoment = (course) => {
  if (!course?.date || !course?.time) return null;
  try {
    return moment(`${course.date} ${course.time}`, 'jYYYY-jMM-jDD HH:mm');
  } catch {
    return null;
  }
};

const usePrivateClassData = () => {
  const [data, setData] = useState(() => {
    try {
      const cached = engine.get('appData', null);
      if (cached && Array.isArray(cached.courses)) return cached;
    } catch {}
    return null;
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [appDataRes, usersRes] = await Promise.all([
          fetch(`${PC_API_BASE}/appData`),
          fetch(`${PC_API_BASE}/users`),
        ]);
        if (!appDataRes.ok) return;
        const appDataRaw = await appDataRes.json();
        const usersRaw = usersRes.ok ? await usersRes.json() : [];

        const fresh = {
          ...appDataRaw,
          users:
            Array.isArray(usersRaw) && usersRaw.length > 0
              ? usersRaw
              : appDataRaw.users || [],
        };
        if (cancelled) return;
        setData(fresh);
        try {
          engine.set('appData', fresh);
        } catch {}
      } catch {}
    };

    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(load, 15000);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const onVis = () => {
      if (document.hidden) stop();
      else {
        load();
        start();
      }
    };

    load();
    start();
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return data;
};

// ================================================================
// Stats data (shared by Hero + Orbitals + Strip)
// ================================================================
const useTeacherStats = () => {
  const { user } = useAuth();
  const { exams, getExamsByTeacher } = useExams();
  const pcData = usePrivateClassData();
  const projects = useMyProjects();

  const teacherCourses = useMemo(() => {
    if (!pcData?.courses || !user?.id) return [];
    return pcData.courses.filter(
      (c) =>
        c.teacherId === user.id &&
        !(c.hiddenByUsers || []).includes(user.id)
    );
  }, [pcData?.courses, user?.id]);

  const liveClass = useMemo(
    () => teacherCourses.find((c) => c.status === PC_STATUS.LIVE),
    [teacherCourses]
  );

  const nextScheduledClass = useMemo(() => {
    const now = moment();
    return teacherCourses
      .filter((c) => c.status === PC_STATUS.SCHEDULED)
      .filter((c) => {
        const sm = pcGetStartMoment(c);
        if (!sm) return false;
        const diffHours = now.diff(sm, 'hours');
        return diffHours < 3 && diffHours > -24 * 7;
      })
      .sort((a, b) => {
        const am = pcGetStartMoment(a)?.valueOf() || 0;
        const bm = pcGetStartMoment(b)?.valueOf() || 0;
        return am - bm;
      })[0];
  }, [teacherCourses]);

  const featuredClass = liveClass || nextScheduledClass;

  const activeClasses = useMemo(
    () =>
      teacherCourses.filter(
        (c) => c.status === PC_STATUS.SCHEDULED || c.status === PC_STATUS.LIVE
      ).length,
    [teacherCourses]
  );

  const activeExams = useMemo(() => {
    if (!user?.id) return 0;
    return getExamsByTeacher(user.id).filter((e) => e.statusType === 'active')
      .length;
  }, [user?.id, getExamsByTeacher, exams]);

  const pendingCorrections = useMemo(() => {
    if (!user?.id) return 0;
    const myExams = getExamsByTeacher(user.id);
    let count = 0;
    myExams.forEach((exam) => {
      if (exam.statusType !== 'correction') return;
      (exam.results || []).forEach((r) => {
        if (r.isEssayGraded === false) count++;
      });
    });
    return count;
  }, [user?.id, getExamsByTeacher, exams]);

  const classAvg = useMemo(() => {
    if (!user?.id) return '—';
    const myExams = getExamsByTeacher(user.id);
    let totalScores = 0;
    let totalResults = 0;
    myExams.forEach((exam) => {
      (exam.results || []).forEach((r) => {
        totalScores += toNumber(r.score);
        totalResults++;
      });
    });
    if (totalResults === 0) return '—';
    return toFaNum((totalScores / totalResults).toFixed(1));
  }, [user?.id, getExamsByTeacher, exams]);

  const classProjects = useMemo(
    () =>
      projects.filter(
        (p) =>
          p.state === PROJECT_STATE.BUILDING ||
          p.state === PROJECT_STATE.PUBLISHED
      ).length,
    [projects]
  );

  const totalStudents = useMemo(() => {
    const uniqueIds = new Set();

    try {
      const members = engine.get('teamMembers', []);
      if (Array.isArray(members)) {
        members.forEach((m) => {
          if (
            m &&
            m.status === 'active' &&
            m.studentId &&
            m.studentId !== user?.id
          ) {
            uniqueIds.add(String(m.studentId));
          }
        });
      }
    } catch {}

    if (pcData?.courses && user?.id) {
      pcData.courses
        .filter(
          (c) =>
            c.teacherId === user.id &&
            !(c.hiddenByUsers || []).includes(user.id)
        )
        .forEach((c) => {
          (c.enrolledStudents || []).forEach((s) => {
            if (s?.userId && s.userId !== user.id) {
              uniqueIds.add(String(s.userId));
            }
          });
        });
    }

    return uniqueIds.size;
  }, [pcData?.courses, user?.id, projects]);

  return {
    liveClass,
    nextScheduledClass,
    featuredClass,
    activeClasses,
    activeExams,
    pendingCorrections,
    classAvg,
    classProjects,
    totalStudents,
  };
};

// ================================================================
// Hero Component
// ================================================================
const HeroCard = ({ navigate, stats }) => {
  const { liveClass, nextScheduledClass, featuredClass } = stats;

  const goTo = () => navigate('/private-class/teacher');

  let pillText = 'کار بعدی شما';
  let heroTitle = 'کلاس بعدی نداری';
  let heroSubtitle = 'یک کلاس خصوصی جدید بساز';
  let heroIcon = 'fa-plus-circle';
  let metaItems = null;
  let ctaText = 'ورود به پنل کلاس';

  if (liveClass) {
    pillText = 'در حال برگزاری';
    heroTitle = liveClass.title || 'کلاس زنده';
    heroIcon = 'fa-video';
    ctaText = 'ورود به کلاس';
    metaItems = (
      <>
        <span>
          <i className="fas fa-clock"></i> همین حالا
        </span>
        <span>
          <i className="fas fa-users"></i> {toFaNum(liveClass.enrolled || 0)}{' '}
          دانش‌آموز
        </span>
      </>
    );
  } else if (nextScheduledClass) {
    const sm = pcGetStartMoment(nextScheduledClass);
    const diffMin = sm ? sm.diff(moment(), 'minutes') : null;
    let when = nextScheduledClass.time || '—';
    if (diffMin !== null && diffMin > 0) {
      if (diffMin < 60) when = `${toFaNum(diffMin)} دقیقه دیگر`;
      else if (diffMin < 24 * 60)
        when = `${toFaNum(Math.round(diffMin / 60))} ساعت دیگر`;
    }
    pillText = 'کار بعدی شما';
    heroTitle = nextScheduledClass.title || 'کلاس بعدی';
    heroIcon = 'fa-chalkboard-teacher';
    ctaText = 'ورود به کلاس';
    metaItems = (
      <>
        <span>
          <i className="far fa-clock"></i> {when}
        </span>
        <span>
          <i className="fas fa-users"></i>{' '}
          {toFaNum(nextScheduledClass.enrolled || 0)} دانش‌آموز
        </span>
      </>
    );
  } else if (featuredClass) {
    heroTitle = featuredClass.title || 'کلاس';
    heroIcon = 'fa-chalkboard-teacher';
  }

  return (
    <section className="th-hero" onClick={goTo} aria-label="کار بعدی">
      <span className="th-hero-pill">
        <span className="th-live-dot"></span>
        {pillText}
      </span>
      <div className="th-hero-icon">
        <i className={`fas ${heroIcon}`}></i>
      </div>
      <h2 className="th-hero-title">{heroTitle}</h2>
      <div className="th-hero-meta">
        {metaItems || (
          <>
            <span>
              <i className="fas fa-info-circle"></i> {heroSubtitle}
            </span>
          </>
        )}
      </div>
      <button
        className="th-hero-cta"
        onClick={(e) => {
          e.stopPropagation();
          goTo();
        }}
      >
        <span>{ctaText}</span>
        <i className="fas fa-arrow-left"></i>
      </button>
    </section>
  );
};

// ================================================================
// Orbitals
// ================================================================
const Orbitals = ({ navigate, stats }) => (
  <div className="th-orbitals">
    <div
      className="th-orbital o1"
      onClick={() => navigate('/exams/teacher?filter=correction')}
    >
      <div className="th-o-icon">
        <i className="fas fa-check-double"></i>
      </div>
      <div className="th-o-num">{toFaNum(stats.pendingCorrections)}</div>
      <div className="th-o-label">تصحیح</div>
    </div>
    <div
      className="th-orbital o2"
      onClick={() => navigate('/exams/teacher')}
    >
      <div className="th-o-icon">
        <i className="fas fa-file-alt"></i>
      </div>
      <div className="th-o-num">{toFaNum(stats.activeExams)}</div>
      <div className="th-o-label">آزمون ها</div>
    </div>
    <div
      className="th-orbital o3"
      onClick={() => navigate('/team-dashboard')}
    >
      <div className="th-o-icon">
        <i className="fas fa-users"></i>
      </div>
      <div className="th-o-num">{toFaNum(stats.activeClasses)}</div>
      <div className="th-o-label">گروه کلاسی</div>
    </div>
  </div>
);

// ================================================================
// PDF Demo
// ================================================================
const PdfDemo = () => {
  const demoRef = useRef(null);
  const [state, setState] = useState({
    exam: '',
    print: '',
    pdf: '',
    arrow0: '',
    arrow1: '',
    playing: false,
    settled: false,
  });

  useEffect(() => {
    const play = () => {
      setState({
        exam: 'active',
        print: '',
        pdf: '',
        arrow0: '',
        arrow1: '',
        playing: true,
        settled: false,
      });

      setTimeout(() => {
        setState((s) => ({
          ...s,
          exam: 'done',
          print: 'active',
          arrow0: 'lit',
        }));
      }, 900);

      setTimeout(() => {
        setState((s) => ({
          ...s,
          print: 'done',
          pdf: 'active',
          arrow1: 'lit',
        }));
      }, 2000);

      setTimeout(() => {
        setState((s) => ({
          ...s,
          pdf: 'done',
          playing: false,
          settled: true,
        }));
      }, 3200);
    };

    play();
    const id = setInterval(play, 5000);
    return () => clearInterval(id);
  }, []);

  const cls = [
    'th-pdf-demo',
    state.playing ? 'playing' : '',
    state.settled ? 'settled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={demoRef}
      className={cls}
      title="آزمونت رو مثل PDF مدرسه چاپ کن"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`th-pd-step th-pd-exam ${state.exam}`}>
        <i className="fas fa-file-lines"></i>
        <span>آزمون</span>
      </div>
      <div className={`th-pd-arrow ${state.arrow0}`}>
        <i className="fas fa-chevron-left"></i>
      </div>
      <div className={`th-pd-step th-pd-print ${state.print}`}>
        <i className="fas fa-print"></i>
        <span>چاپ</span>
      </div>
      <div className={`th-pd-arrow ${state.arrow1}`}>
        <i className="fas fa-chevron-left"></i>
      </div>
      <div className={`th-pd-step th-pd-pdf ${state.pdf}`}>
        <i className="fas fa-file-pdf"></i>
        <span>PDF</span>
      </div>
    </div>
  );
};

// ================================================================
// Quick Grid
// ================================================================
const QuickGrid = ({ navigate, weather }) => {
  const weatherSub = weather
    ? `${weather.city} · ${toFaNum(weather.temp)}°`
    : 'در حال بارگذاری…';

  return (
    <div className="th-quick-grid">
      <div
        className="th-tile tile-ai"
        onClick={() => navigate('/exams/create-ai')}
      >
        <div className="th-t-icon">
          <i className="fas fa-wand-magic-sparkles"></i>
        </div>
        <div>
          <div className="th-t-title">ساخت آزمون با هوش مصنوعی</div>
          <div className="th-t-sub">تولید خودکار سوالات</div>
          <PdfDemo />
        </div>

      </div>

      <div className="th-tile t-books" onClick={() => navigate('/books')}>
        <div className="th-t-icon">
          <i className="fas fa-book"></i>
        </div>
        <div>
          <div className="th-t-title">کتاب‌ها</div>
          <div className="th-t-sub">محتوای درسی</div>
        </div>
      </div>

      <div className="th-tile t-reports" onClick={() => navigate('/exams/teacher')}>
        <div className="th-t-icon">
          <i className="fas fa-chart-bar"></i>
        </div>
        <div>
          <div className="th-t-title">کارنامه</div>
          <div className="th-t-sub">نتایج دانش‌آموزان</div>
        </div>
      </div>

      <div
        className="th-tile t-private"
        onClick={() => navigate('/private-class/teacher')}
      >
        <div className="th-t-icon">
          <i className="fas fa-video"></i>
        </div>
        <div>
          <div className="th-t-title">کلاس خصوصی</div>
          <div className="th-t-sub">مدیریت جلسات</div>
        </div>
      </div>

      <div
        className="th-tile t-weather"
        onClick={() => navigate('/WeatherPage')}
      >
        <div className="th-t-icon">
          <i className="fas fa-cloud-sun"></i>
        </div>
        <div>
          <div className="th-t-title">آب‌وهوا</div>
          <div className="th-t-sub">{weatherSub}</div>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// Stats Strip
// ================================================================
const StatsStrip = ({ stats }) => (
  <div className="th-stats-strip">
    <div className="th-strip-item">
      <div className="th-strip-val">{toFaNum(stats.activeClasses)}</div>
      <div className="th-strip-lbl">کلاس فعال</div>
    </div>
    <div className="th-strip-item">
      <div className="th-strip-val">{toFaNum(stats.classProjects)}</div>
      <div className="th-strip-lbl">پروژه</div>
    </div>
    <div className="th-strip-item">
      <div className="th-strip-val s">{stats.classAvg}</div>
      <div className="th-strip-lbl">میانگین نمره کلاس</div>
    </div>
    <div className="th-strip-item">
      <div className="th-strip-val">{toFaNum(stats.totalStudents)}</div>
      <div className="th-strip-lbl">دانش‌آموزان من</div>
    </div>
  </div>
);

// ================================================================
// Inner Dashboard (needs BooksEngineProvider)
// ================================================================
const TeacherHomeInner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const stats = useTeacherStats();
  const weather = useWeatherTile(user);

  const userName =
    user?.firstName ||
    user?.name ||
    user?.username ||
    'استاد';

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const greeting = getPersianGreeting();
  const dateStr = now.toLocaleDateString('fa-IR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const timeStr = now.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="th-container">
      <header className="th-greeting">
        <h1>
          {greeting}، <span>{userName}</span> 👋
        </h1>
        <div className="th-sub">
          {dateStr} · {timeStr}
        </div>
      </header>

      <HeroCard navigate={navigate} stats={stats} />
      <Orbitals navigate={navigate} stats={stats} />

      <div className="th-section-label">دسترسی سریع</div>
      <QuickGrid navigate={navigate} weather={weather} />

      <StatsStrip stats={stats} />
    </div>
  );
};

// ================================================================
// Root
// ================================================================
const TeacherHome = () => {
  const { user } = useAuth();

  // Theme sync — from Navbar2 shared key
  const [isDark, setIsDark] = useState(() => {
    try {
      return (
        engine.getRaw('navbar2-drawer-dark') === '1' ||
        localStorage.getItem('navbar2-drawer-dark') === '1'
      );
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const readTheme = () => {
      try {
        const current =
          engine.getRaw('navbar2-drawer-dark') === '1' ||
          localStorage.getItem('navbar2-drawer-dark') === '1';
        setIsDark((prev) => (prev !== current ? current : prev));
      } catch {}
    };

    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') readTheme();
    };
    const handleVisibility = () => {
      if (!document.hidden) readTheme();
    };
    const handleThemeEvent = () => readTheme();

    window.addEventListener('storage', handleStorage);
    window.addEventListener('theme-change', handleThemeEvent);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('theme-change', handleThemeEvent);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Time-based theme
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());
  useEffect(() => {
    const t = setInterval(() => setTimeOfDay(getTimeOfDay()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const effectiveTime = isDark ? 'night' : timeOfDay;

  const rootClass = `teacher-home ${isDark ? 'dark-mode' : ''}`;

  return (
    <div className={rootClass} data-time={effectiveTime}>
      <div className="th-bg-layer" />
      <div className="th-bg-blob b1" />
      <div className="th-bg-blob b2" />

      {user ? (
        <BooksEngineProvider user={user}>
          <TeacherHomeInner />
        </BooksEngineProvider>
      ) : (
        <div className="th-container" />
      )}
    </div>
  );
};

export default TeacherHome;